import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SelectMultiline } from '../../components/Select';
import { constructionStatusDescriptors } from '../../constants/descriptors';
import { getOffersCount } from '../../actions/offersData';
import { ECompletionYearType, CURRENT_YEAR } from '../../constants/avaliableYears';
import { isDescriptorActive } from '../../utils/filters';
import { IApplicationState } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { EFiltersActions } from '../../actions/filters';

function createOptions() {
  return [
    {
      id: String(ECompletionYearType.HandedOver),
      label: 'Дом сдан',
    },
    {
      id: String(ECompletionYearType.CurrentYear),
      label: `${CURRENT_YEAR}`,
    },
    {
      id: String(ECompletionYearType.InOneYear),
      label: `${CURRENT_YEAR + 1}`,
    },
    {
      id: String(ECompletionYearType.InTwoYears),
      label: `${CURRENT_YEAR + 2}`,
    },
    {
      id: String(ECompletionYearType.InThreeYears),
      label: `${CURRENT_YEAR + 3}`,
    },
    {
      id: String(ECompletionYearType.Later),
      label: `${CURRENT_YEAR + 4} и позже`,
    },
  ];
}

export const FinishDate: React.FC = React.memo(() => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);
  const options = React.useMemo(() => createOptions(), []);

  const activeDescriptors = React.useMemo(
    () => constructionStatusDescriptors.filter(descriptor => isDescriptorActive(descriptor)(jsonQuery)),
    [jsonQuery],
  );

  const selectedValues = React.useMemo(() => activeDescriptors.map(d => d.state), [activeDescriptors]);

  const getText = React.useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        return undefined;
      }

      const labels = values
        .map(value => {
          const option = options.find(opt => opt.id === value);

          return option ? option.label : null;
        })
        .filter(Boolean);

      return labels.join(', ');
    },
    [options],
  );

  const text = React.useMemo(() => getText(selectedValues), [getText, selectedValues]);

  const handleChange = React.useCallback(
    (value: string) => {
      const descriptor = constructionStatusDescriptors.find(d => d.state === value);

      if (descriptor) {
        // Проверяем, активен ли дескриптор
        const isActive = activeDescriptors.some(d => d.state === value);

        // Если это "Дом сдан" или "2030 и позже" и они уже активны, диспатчим clear action
        if (value === String(ECompletionYearType.HandedOver) && isActive) {
          dispatch({
            type: EFiltersActions.clear,
            key: 'is_finished',
            value: true,
          });
        } else if (value === String(ECompletionYearType.Later) && isActive) {
          dispatch({
            type: EFiltersActions.clear,
            key: 'yeargte',
            value: CURRENT_YEAR + ECompletionYearType.Later,
          });
        } else {
          dispatch({
            type: `${descriptor.name}/${descriptor.state}`,
            value,
          });
        }
        dispatch(getOffersCount());
      }
    },
    [dispatch, activeDescriptors],
  );

  return (
    <SelectMultiline
      placeholder="Срок сдачи"
      options={options}
      value={selectedValues}
      text={text}
      onChange={handleChange}
    />
  );
});

FinishDate.displayName = 'FinishDate';
